/**
 * /quotes/:id/success — confirmation screen after submitting a quote.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail, Package as PackageIcon } from "lucide-react";

import { getMyQuote } from "../lib/api";

function formatINR(n) {
  if (n == null) return "–";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

export default function QuoteSuccess() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getMyQuote(id)
      .then((data) => {
        if (!cancelled) setQuote(data);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your request. Try refreshing.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const shortRef = quote?.short_ref || (id ? `SK-${id.replace(/-/g, "").slice(-8).toUpperCase()}` : "");
  const occ = quote?.occasion || "shoot";
  const dateSummary = quote?.date_summary;
  const locSummary = quote?.location_summary;
  const refCount = (quote?.reference_image_ids || []).length;
  const scopeBits = (quote?.payload?.scope || {});

  return (
    <section
      data-testid="quote-success"
      className="mx-auto max-w-2xl px-5 pb-24 pt-12 sm:px-6 sm:pt-20"
    >
      <div className="flex items-center gap-3 text-[#4F8060]">
        <CheckCircle2 className="h-7 w-7" />
        <p className="text-[10px] uppercase tracking-[0.32em]">Request received</p>
      </div>
      <h1 className="mt-4 font-display text-4xl text-[#1A1A1A] sm:text-5xl">
        We have your request.
      </h1>
      <p className="mt-4 text-base text-[#1A1A1A]/65 sm:text-lg">
        Someone from our team will get back to you within{" "}
        <span className="font-medium text-[#1A1A1A]">24 business hours</span> with a
        tailored quote.
      </p>

      <div
        data-testid="quote-success-ref"
        className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#1A1A1A]/15 bg-white px-5 py-2.5"
      >
        <span className="text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/55">
          Request ID
        </span>
        <span className="font-mono text-sm tracking-widest text-[#1A1A1A]">
          {shortRef}
        </span>
      </div>

      {quote?.source_package?.name ? (
        <div
          data-testid="quote-success-source-package"
          className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-[#C8A24B]/40 bg-[#C8A24B]/10 px-5 py-3"
        >
          <PackageIcon className="h-4 w-4 text-[#9A7A2F]" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#9A7A2F]">
              Requested package
            </p>
            <p className="text-sm text-[#1A1A1A]">
              <span className="font-medium">{quote.source_package.name}</span>
              {quote.source_package.base_price_inr ? (
                <span className="text-[#1A1A1A]/65">
                  {" "}— starting {formatINR(quote.source_package.base_price_inr)}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="mt-6 text-sm text-[#B0413E]">{error}</p>
      ) : (
        <div
          data-testid="quote-success-summary"
          className="mt-10 rounded-3xl border border-[#1A1A1A]/10 bg-white p-6 sm:p-8"
        >
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
            Summary
          </p>
          <h2 className="mt-2 font-display text-2xl capitalize text-[#1A1A1A]">
            {occ.replaceAll("_", " ")}
          </h2>
          <dl className="mt-5 space-y-3 text-sm">
            <Row label="Date" value={dateSummary} />
            <Row label="Location" value={locSummary} />
            {scopeBits.guest_count_band ? (
              <Row label="Guests" value={scopeBits.guest_count_band} />
            ) : null}
            {scopeBits.duration ? (
              <Row label="Duration" value={scopeBits.duration} />
            ) : null}
            {scopeBits.video_deliverables ? (
              <Row label="Video" value={scopeBits.video_deliverables} />
            ) : null}
            <Row label="References" value={`${refCount} attached`} />
            {quote?.estimated_range_low != null && quote?.estimated_range_high != null ? (
              <Row
                label="Estimated range"
                value={`${formatINR(quote.estimated_range_low)} – ${formatINR(quote.estimated_range_high)}`}
              />
            ) : null}
          </dl>
          <p className="mt-6 text-xs text-[#1A1A1A]/55">
            This estimate is indicative. Your final quote may adjust based on the
            brief our team puts together.
          </p>
        </div>
      )}

      <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Link
          to="/track"
          data-testid="quote-success-track"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3 text-xs uppercase tracking-[0.22em] text-[#FAF7F2] transition hover:bg-[#2a2a2a]"
        >
          Track your request
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/"
          data-testid="quote-success-browse"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1A1A1A]/15 px-6 py-3 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/80 transition hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
        >
          Browse more inspiration
        </Link>
      </div>

      <p className="mt-10 flex items-center gap-2 text-xs text-[#1A1A1A]/55">
        <Mail className="h-3.5 w-3.5" />
        Need to update something?{" "}
        <a
          href="mailto:hello@shutterkart.com"
          className="font-medium text-[#1A1A1A] underline decoration-[#C8A24B] underline-offset-4"
        >
          hello@shutterkart.com
        </a>
      </p>
    </section>
  );
}

function Row({ label, value }) {
  if (value == null || value === "") return null;
  // Defensive: never let a non-primitive sneak into a JSX child. If a future
  // API change returns an object here, fall back to a string label rather than
  // crashing the page with "Objects are not valid as a React child".
  let display;
  if (typeof value === "string" || typeof value === "number") {
    display = String(value).replaceAll("_", " ");
  } else if (Array.isArray(value)) {
    display = value.filter((v) => typeof v === "string" || typeof v === "number").join(", ");
  } else {
    return null;
  }
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#1A1A1A]/5 pb-3 last:border-0 last:pb-0">
      <dt className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">{label}</dt>
      <dd className="max-w-[60%] text-right capitalize text-[#1A1A1A]">{display}</dd>
    </div>
  );
}
